import type {
  SettingsGroupEmployee,
  SettingsTeamDetails,
  SettingsTeamSkill,
} from '../../../../queries/skill-administration';
import { getCustomSkillDisplayText } from '../custom-skills-settings/custom-skill-model';
import { getPersonName } from '../settings-people/employee-utils';

function getGroupPeople(userUuids: string[], employees: SettingsGroupEmployee[] | null) {
  const profiles = new Map((employees ?? []).map((employee) => [employee.userUuid, employee]));
  return [...new Set(userUuids)].map((userUuid) => {
    const profile = profiles.get(userUuid);
    return {
      userUuid,
      name: (profile && getPersonName({ name: profile.fullName, email: profile.email })) || 'Unknown employee',
      imageUrl: profile?.imageUrl ?? undefined,
      initials: profile ? undefined : '?',
    };
  });
}

export function getTeamDetailModel(group: SettingsTeamDetails) {
  const owners = getGroupPeople(group.managers, group.managerEmployees);
  const members = getGroupPeople(group.members, group.memberEmployees);
  const skillCount = new Set(group.skillUuids).size + new Set(group.builtInSkillCommandHandles).size;
  return { owners, members, skillCount };
}

export function getTeamSkillRows(group: SettingsTeamDetails, skillDetails: SettingsTeamSkill[]) {
  const customSkills = new Map(skillDetails.map((skill) => [skill.uuid, skill]));
  return [
    ...[...new Set(group.skillUuids)].map((uuid) => {
      const skill = customSkills.get(uuid);
      const owner = skill?.owner;
      const ownerName = owner && getPersonName(owner);
      return {
        kind: 'custom' as const,
        uuid,
        key: `custom:${uuid}`,
        title: skill ? `/${skill.command}` : 'Unavailable skill',
        description: skill
          ? getCustomSkillDisplayText(skill.frontmatter.summary, skill.frontmatter.description)
          : undefined,
        attribution: ownerName ? `Owned by ${ownerName}` : 'Owner unavailable',
      };
    }),
    ...[...new Set(group.builtInSkillCommandHandles)].map((command) => ({
      kind: 'built-in' as const,
      commandHandle: command,
      key: `built-in:${command}`,
      title: `/${command}`,
      description: undefined,
      attribution: 'WTW skill',
    })),
  ];
}

export type TeamSkillRow = ReturnType<typeof getTeamSkillRows>[number];
